import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminAgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [monthsFilter, setMonthsFilter] = useState(12);

  useEffect(() => {
    if (id) {
      loadAgentDetails();
    }
  }, [id]);

  const loadAgentDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading agent details for ID:', id);
      const response = await adminApiService.getAgentById(id);
      console.log('📊 Agent Details API Response:', response);
      
      if (response.success) {
        setAgent(response.data);
        console.log('✅ Agent details loaded successfully');
      } else {
        console.error('❌ Agent Details API returned error:', response.message);
        toast.error(response.message || 'Failed to load agent details');
        navigate('/admin/dashboard/account-manager');
      }
    } catch (error) {
      console.error('❌ Agent details load error:', error);
      toast.error('Failed to load agent details');
      navigate('/admin/dashboard/account-manager');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      console.log('✅ Approving agent ID:', id);
      const response = await adminApiService.approveAgent(id);
      
      if (response.success) {
        toast.success('Agent approved successfully');
        await loadAgentDetails();
        setShowApproveModal(false);
      } else {
        toast.error(response.message || 'Failed to approve agent');
      }
    } catch (error) {
      console.error('❌ Approve agent error:', error);
      toast.error('Failed to approve agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisapprove = async () => {
    try {
      setActionLoading(true);
      console.log('❌ Disapproving agent ID:', id);
      const response = await adminApiService.disapproveAgent(id);
      
      if (response.success) {
        toast.success('Agent disapproved successfully');
        await loadAgentDetails();
        setShowDisapproveModal(false);
      } else {
        toast.error(response.message || 'Failed to disapprove agent');
      }
    } catch (error) {
      console.error('❌ Disapprove agent error:', error);
      toast.error('Failed to disapprove agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setActionLoading(true);
      console.log('🟢 Activating agent ID:', id);
      const response = await adminApiService.activateAgent(id);
      
      if (response.success) {
        toast.success('Agent activated successfully');
        await loadAgentDetails();
        setShowActivateModal(false);
      } else {
        toast.error(response.message || 'Failed to activate agent');
      }
    } catch (error) {
      console.error('❌ Activate agent error:', error);
      toast.error('Failed to activate agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setActionLoading(true);
      console.log('🔴 Deactivating agent ID:', id);
      const response = await adminApiService.deactivateAgent(id);
      
      if (response.success) {
        toast.success('Agent deactivated successfully');
        await loadAgentDetails();
        setShowDeactivateModal(false);
      } else {
        toast.error(response.message || 'Failed to deactivate agent');
      }
    } catch (error) {
      console.error('❌ Deactivate agent error:', error);
      toast.error('Failed to deactivate agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewWalletTransactions = async () => {
    try {
      setWalletLoading(true);
      setShowWalletModal(true);
      
      const response = await adminApiService.getAgentWalletTransactions(agent.email, agent.id, monthsFilter);
      
      if (response.success) {
        setWalletTransactions(response.data || []);
      } else {
        toast.error(response.message || 'Failed to load wallet transactions');
      }
    } catch (error) {
      console.error('❌ Error loading wallet transactions:', error);
      toast.error('Failed to load wallet transactions');
    } finally {
      setWalletLoading(false);
    }
  };

  const getStatusBadge = (approved) => {
    if (approved === true) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <i className="fas fa-check-circle mr-2"></i>
          APPROVED
        </span>
      );
    } else if (approved === false) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <i className="fas fa-times-circle mr-2"></i>
          DISAPPROVED
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <i className="fas fa-clock mr-2"></i>
          PENDING
        </span>
      );
    }
  };

  const getActiveBadge = (isActive) => {
    if (isActive === true) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <i className="fas fa-check-circle mr-2"></i>
          ACTIVE
        </span>
      );
    } else if (isActive === false) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <i className="fas fa-times-circle mr-2"></i>
          INACTIVE
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          <i className="fas fa-clock mr-2"></i>
          PENDING
        </span>
      );
    }
  };

  const filteredTransactions = walletTransactions.filter(txn => {
    if (filterType === 'ALL') return true;
    return txn.type === filterType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Agent Not Found</h2>
          <p className="text-gray-600 mb-4">The requested agent details could not be found.</p>
          <button
            onClick={() => navigate('/admin/dashboard/account-manager')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
          >
            Back to Agent Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Agent Details</h1>
              <p className="text-gray-600">View and manage agent information</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard/account-manager')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Agents
            </button>
          </div>
        </div>

        {/* Agent Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Agent Information</h3>
              <div className="flex items-center space-x-4">
                <div className="text-lg font-semibold text-green-600">
                  💰 Wallet Balance: ₹{agent.balanceAmt || '0.00'}
                </div>
                <button
                  onClick={handleViewWalletTransactions}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                >
                  View Transactions
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-2">
              {getStatusBadge(agent.approved)}
              {getActiveBadge(agent.isActive)}
            </div>
          </div>

          {/* Agent Details Form */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={agent.firstName || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={agent.lastName || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={agent.email || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={agent.phoneNumber || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Agent ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent ID
                </label>
                <input
                  type="text"
                  value={agent.agentId || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={agent.role || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={agent.city || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={agent.state || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={agent.country || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* ZIP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={agent.zip || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* PAN Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PAN Number
                </label>
                <input
                  type="text"
                  value={agent.panNumber || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* GST Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number
                </label>
                <input
                  type="text"
                  value={agent.gstNumber || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Udyam Aadhar Registration Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Udyam Aadhar Reg. Number
                </label>
                <input
                  type="text"
                  value={agent.udyamAadharRegNo || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex justify-center space-x-4">
              {/* Approval Actions */}
              {agent.approved === true && (
                <button
                  onClick={() => setShowDisapproveModal(true)}
                  disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-times mr-2"></i>
                      Disapprove
                    </>
                  )}
                </button>
              )}

              {agent.approved === false && (
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      Approve
                    </>
                  )}
                </button>
              )}

              {/* Activation Actions */}
              {agent.isActive === true && (
                <button
                  onClick={() => setShowDeactivateModal(true)}
                  disabled={actionLoading}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-pause mr-2"></i>
                      Deactivate
                    </>
                  )}
                </button>
              )}

              {agent.isActive === false && (
                <button
                  onClick={() => setShowActivateModal(true)}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-play mr-2"></i>
                      Activate
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Approve Agent
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to approve this agent? This will grant them access to the system.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Yes, Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disapprove Modal */}
      {showDisapproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Disapprove Agent
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to disapprove this agent? This will revoke their system access.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDisapproveModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisapprove}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Yes, Disapprove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activate Modal */}
      {showActivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Activate Agent
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to activate this agent? This will enable their account.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowActivateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleActivate}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Yes, Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Deactivate Agent
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to deactivate this agent? This will disable their account.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeactivateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivate}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Yes, Deactivate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Transactions Modal */}
      {showWalletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    <i className="fas fa-wallet mr-2"></i>
                    Wallet Transaction History
                  </h3>
                  <div className="mt-2 flex space-x-2">
                    <button 
                      className={`px-3 py-1 text-xs rounded-full ${filterType === 'ALL' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      onClick={() => setFilterType('ALL')}
                    >
                      All
                    </button>
                    <button 
                      className={`px-3 py-1 text-xs rounded-full ${filterType === 'CREDIT' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                      onClick={() => setFilterType('CREDIT')}
                    >
                      Credit
                    </button>
                    <button 
                      className={`px-3 py-1 text-xs rounded-full ${filterType === 'DEBIT' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                      onClick={() => setFilterType('DEBIT')}
                    >
                      Debit
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <select 
                    className="text-sm border border-gray-300 rounded-md px-2 py-1"
                    value={monthsFilter}
                    onChange={(e) => setMonthsFilter(parseInt(e.target.value))}
                  >
                    <option value="3">Last 3 Months</option>
                    <option value="6">Last 6 Months</option>
                    <option value="9">Last 9 Months</option>
                    <option value="12">Last 12 Months</option>
                  </select>
                  <button 
                    onClick={() => setShowWalletModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {walletLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading transactions...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Debit</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hotel</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        filteredTransactions.map((txn, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{txn.transactionId || '-'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{txn.transactionDate || '-'}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                txn.modeOfPayment === 'Booking' ? 'bg-gray-100 text-gray-800' :
                                txn.modeOfPayment === 'Manual' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {txn.modeOfPayment || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">{txn.credit || '-'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-red-600">{txn.debit || '-'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{txn.totalBalance || '-'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              {txn.bookingId ? (
                                <a href={`/admin/dashboard/booking-details/${txn.bookingId}`} 
                                   className="text-blue-600 hover:text-blue-800 font-medium hover:underline">
                                  {txn.bookingId}
                                </a>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {txn.bookingStatus ? (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  txn.bookingStatus === 'BOOKED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {txn.bookingStatus}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{txn.hotelName || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAgentDetails;